import React, { useState, useEffect, ChangeEvent } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Typography,
} from '@material-ui/core'
import { TreeItem, TreeView } from '@material-ui/lab'
import { ChevronRight, ExpandMore } from '@material-ui/icons'
import _ from 'lodash'

import { useCategoriesApi } from '../../graphql/hooks/categories'
import { ICategory } from '../../graphql/types/categories'
import { ISelectProps } from './SelectField'

interface IProps extends Omit<ISelectProps, 'options' | 'onChange'> {
  value?: ICategory[]
  onChange: (items: ICategory[]) => void
  helperText?: string
}

interface ICategorySelectProps {
  label: string
  value: ICategory
  selected?: boolean
  onSelect: (item: ICategory) => void
}

const CategorySelectItem: React.FC<ICategorySelectProps> = ({ label, value, selected, onSelect }) => {
  return (
    <FormControlLabel
      control={<Checkbox checked={selected} onChange={() => onSelect(value)} value={value} />}
      label={label}
    />
  )
}

const renderCategories = (
  categories: ICategory[],
  selectedCategories: ICategory[],
  onSelect: (item: ICategory) => void
) => {
  return categories.map(category => {
    const selected = !!selectedCategories.find(c => c.id === category.id)
    const isLeaf = !category.children || category.children.length === 0

    return (
      <TreeItem
        key={`cat-${category.id}`}
        nodeId={`${category.id}`}
        label={
          isLeaf ? (
            <CategorySelectItem value={category} selected={selected} label={category.title} onSelect={onSelect} />
          ) : (
            <Box p={1}>
              <Typography>{category.title}</Typography>
            </Box>
          )
        }
      >
        {category.children && category.children.length > 0
          ? renderCategories(category.children, selectedCategories, onSelect)
          : null}
      </TreeItem>
    )
  })
}

export const CategorySelectField: React.FC<IProps> = ({ helperText, error, value = [], onChange, placeholder }) => {
  const { loading, categoriesTree: categories } = useCategoriesApi()
  const [open, setOpen] = useState<boolean>(false)
  const [selectedValue, setSelectValue] = useState<ICategory[]>(value)
  const [expanded, setExpanded] = useState<string[]>([])

  const toggle = () => {
    setOpen(!open)
  }

  const handleToggleTree = (_event: ChangeEvent<{}>, nodeIds: string[]) => {
    setExpanded(_.without(nodeIds, ...expanded))
  }

  const handleSelect = (item: ICategory) => {
    setExpanded([...expanded, `${item.id}`])

    if (selectedValue.find(c => c.id === item.id)) {
      setSelectValue(selectedValue.filter(c => c.id !== item.id))
    } else {
      setSelectValue(_.uniqBy([...selectedValue, item], 'id'))
    }
  }

  const handleSubmit = () => {
    onChange(selectedValue)
    toggle()
  }

  useEffect(() => {
    if (open) {
      setSelectValue(value || [])
    }
  }, [open, value])

  if (loading) {
    return <CircularProgress />
  }

  return (
    <Box my={2}>
      <Button
        onClick={toggle}
        variant='outlined'
        style={{
          borderColor: error ? 'red' : undefined,
          color: error ? 'red' : undefined,
        }}
      >
        Выбрать категорию
      </Button>

      {(!!error || !!helperText) && <FormHelperText error={!!error}>{helperText?.toString() || ''}</FormHelperText>}

      <Box display='flex' flexWrap='wrap' mt={1}>
        {value.map(v => {
          return (
            <Box key={`value-${v.id}`} mr={1} mb={1}>
              <Chip
                label={v.title}
                onDelete={() => {
                  onChange(value?.filter(vv => vv.id !== v.id) || [])
                }}
                variant='outlined'
              />
            </Box>
          )
        })}
      </Box>
      <Dialog open={open} onClose={toggle} maxWidth='md' fullWidth PaperProps={{ style: { minHeight: '500px' } }}>
        <DialogTitle>{placeholder ?? 'Выберите категорию'}</DialogTitle>
        <DialogContent>
          <TreeView
            defaultCollapseIcon={<ExpandMore />}
            defaultExpandIcon={<ChevronRight />}
            expanded={expanded}
            onNodeToggle={handleToggleTree}
          >
            {renderCategories(categories, selectedValue, handleSelect)}
          </TreeView>
        </DialogContent>
        <DialogActions>
          <Button onClick={toggle}>Отмена</Button>
          <Button onClick={handleSubmit} variant='contained' color='primary'>
            Применить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
